// The rail's quick filter: "All", or one calendar for now. Focus blocks and tasks always show (they
// belong to no calendar). The choice is remembered on this device only; storage can be missing or
// refuse, so every read and write is guarded and the rail works without it.
import type { AgendaItem, CalendarSource } from "@shared/agenda";

export const FILTER_STORAGE_KEY = "mm.day.calendarFilter";

/** Chips only help when there is more than one calendar to choose between. */
export function showCalendarChips(calendars: readonly CalendarSource[]): boolean {
  return calendars.length > 1;
}

/** The filter in force: a remembered calendar that is still shown, else all (null). */
export function activeFilter(
  remembered: string | null,
  calendars: readonly CalendarSource[],
): string | null {
  if (!remembered || !showCalendarChips(calendars)) return null;
  return calendars.some((c) => c.key === remembered) ? remembered : null;
}

/** The day seen through the filter: one calendar's events plus every focus block and task. */
export function filterAgenda(items: readonly AgendaItem[], key: string | null): AgendaItem[] {
  if (!key) return [...items];
  return items.filter((i) => i.kind !== "event" || i.source?.key === key);
}

export function readFilter(
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
): string | null {
  try {
    return storage?.getItem(FILTER_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function saveFilter(
  key: string | null,
  storage: Pick<Storage, "setItem" | "removeItem"> | null = safeStorage(),
): void {
  try {
    if (key) storage?.setItem(FILTER_STORAGE_KEY, key);
    else storage?.removeItem(FILTER_STORAGE_KEY);
  } catch {
    // Private windows and blocked storage: the filter simply isn't remembered.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
